'use client'

import React, { useState } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Button } from '@/app/components/common/Button'
import { Spinner } from '@/app/components/common/Spinner'
import { isValidPhoneNumber } from '@/app/components/common/PhoneInput'
import { logError, logWarn, debug } from '@/lib/logging'

interface PaymentFormProps {
  baseAmount: number
  surcharge: number
  protectionFee: number
  protectionType: 'none' | 'transfer' | 'refund'
  packageId: string
  providerId: string
  orgId: string
  paymentType: 'full' | 'split'
  paymentMethod: 'card' | 'ach'
  contactInfo: {
    name: string
    email: string
    phone: string
    address1: string
    address2: string
    city: string
    state: string
    zip: string
    dueDate?: string
  }
  onPaymentSuccess: (paymentId: string, isProcessing?: boolean) => void
  onPaymentError: (error: string) => void
}

export function PaymentForm({
  baseAmount,
  surcharge,
  protectionFee,
  protectionType,
  packageId,
  providerId,
  orgId,
  paymentType,
  paymentMethod,
  contactInfo,
  onPaymentSuccess,
  onPaymentError,
}: PaymentFormProps) {
  const totalAmount = baseAmount + surcharge + protectionFee
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [verifyingPayment, setVerifyingPayment] = useState(false)

  const pollPaymentStatus = async (paymentId: string, maxAttempts = 10, interval = 2000) => {
    debug('ui', `Starting to poll payment status for payment: ${paymentId}`)

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(`/api/payment/status/${paymentId}`)
        if (response.ok) {
          const data = await response.json()
          debug('ui', `Poll attempt ${attempt}: Payment status check returned`, data)

          if (data.status === 'completed') {
            debug('ui', `Payment confirmed as completed after ${attempt} attempts`)
            return true
          }
        }

        // Wait before next attempt (except on last attempt)
        if (attempt < maxAttempts) {
          debug(
            'ui',
            `Payment not yet confirmed, waiting ${interval}ms before attempt ${attempt + 1}`
          )
          await new Promise((resolve) => setTimeout(resolve, interval))
        }
      } catch (error) {
        logError(`Payment status check attempt ${attempt} failed:`, error)
      }
    }

    debug(
      'ui',
      `Payment status polling completed after ${maxAttempts} attempts - payment may still be processing`
    )
    return false
  }

  const handleCardPayment = async (clientSecret: string, paymentId: string) => {
    if (!stripe || !elements) {
      throw new Error('Stripe is not initialized')
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      throw new Error('Card element not found')
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: contactInfo.name,
          email: contactInfo.email,
          phone: contactInfo.phone,
          address: {
            line1: contactInfo.address1,
            line2: contactInfo.address2 || undefined,
            city: contactInfo.city,
            state: contactInfo.state,
            postal_code: contactInfo.zip,
            country: 'US',
          },
        },
      },
    })

    if (stripeError) {
      logError('Stripe payment error:', stripeError)
      throw new Error(stripeError.message || 'Payment failed')
    }

    if (paymentIntent?.status === 'succeeded') {
      debug('ui', `Card payment succeeded: ${paymentIntent.id}`)

      // Try to verify payment status immediately (fallback for when webhooks don't work in development)
      try {
        await fetch('/api/payment/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id, paymentId }),
        })
        debug('ui', 'Payment status verification completed')
      } catch (verifyError) {
        logError('Payment verification failed, will rely on polling:', verifyError)
      }

      // Poll payment status before redirecting
      setProcessing(false)
      setVerifyingPayment(true)

      const paymentConfirmed = await pollPaymentStatus(paymentId)
      setVerifyingPayment(false)

      if (paymentConfirmed) {
        onPaymentSuccess(paymentId)
      } else {
        // Payment succeeded with Stripe but database not yet updated
        debug('ui', 'Proceeding with payment success despite database not yet updated')
        onPaymentSuccess(paymentId)
      }
    } else {
      throw new Error('Payment was not completed successfully')
    }
  }

  const handleAchPayment = async (clientSecret: string, paymentId: string) => {
    if (!stripe) {
      throw new Error('Stripe is not initialized')
    }

    // Step 1: Collect bank account via Financial Connections modal
    const { paymentIntent, error: collectError } = await stripe.collectBankAccountForPayment({
      clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        payment_method_data: {
          billing_details: {
            name: contactInfo.name,
            email: contactInfo.email,
          },
        },
      },
      expand: ['payment_method'],
    })

    if (collectError) {
      logError('Bank account collection error:', collectError)
      throw new Error(collectError.message || 'Failed to collect bank account')
    }

    if (!paymentIntent) {
      throw new Error('No payment intent returned')
    }

    // Step 2: Check if we need microdeposit verification
    if (paymentIntent.status === 'requires_action') {
      // Microdeposits will be sent - user will receive email from Stripe
      // They can verify via Stripe's hosted page or find link in their dashboard
      debug('ui', 'ACH payment requires microdeposit verification')
      onPaymentSuccess(paymentId, true) // isProcessing = true
      return
    }

    // Step 3: Confirm the payment (after user accepts mandate in Financial Connections)
    if (paymentIntent.status === 'requires_confirmation') {
      const { error: confirmError, paymentIntent: confirmedIntent } =
        await stripe.confirmUsBankAccountPayment(clientSecret)

      if (confirmError) {
        logError('ACH confirmation error:', confirmError)
        throw new Error(confirmError.message || 'Payment confirmation failed')
      }

      if (confirmedIntent?.status === 'processing') {
        debug('ui', `ACH payment processing: ${confirmedIntent.id}`)
        // Hit status endpoint so server adds package while payment settles
        await fetch(`/api/payment/status/${paymentId}`).catch((err) =>
          logWarn('Failed to poll payment status for ACH package activation:', err)
        )
        onPaymentSuccess(paymentId, true) // isProcessing = true
        return
      }
    }

    // If we got here with processing status, ACH is underway
    if (paymentIntent.status === 'processing') {
      debug('ui', `ACH payment processing: ${paymentIntent.id}`)
      // Hit status endpoint so server adds package while payment settles
      await fetch(`/api/payment/status/${paymentId}`).catch((err) =>
        logWarn('Failed to poll payment status for ACH package activation:', err)
      )
      onPaymentSuccess(paymentId, true) // isProcessing = true
      return
    }

    throw new Error('Unexpected payment status')
  }

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stripe || !elements) {
      onPaymentError('Stripe is not initialized')
      return
    }

    // For card payments, validate card element exists
    if (paymentMethod === 'card') {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        onPaymentError('Card element not found')
        return
      }
    }

    // Validate phone number before processing payment
    if (!contactInfo.phone || !isValidPhoneNumber(contactInfo.phone)) {
      onPaymentError('Please provide a valid 10-digit phone number')
      return
    }

    setProcessing(true)
    setCardError(null)
    setPaymentError(null)

    try {
      debug(
        'ui',
        `Processing ${paymentMethod} payment: $${baseAmount} + $${surcharge} surcharge + $${protectionFee} protection = $${totalAmount} for package ${packageId}`
      )

      // Create purchase and payment intent
      const response = await fetch('/api/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId,
          providerId,
          orgId,
          amount: totalAmount,
          baseAmount,
          surcharge,
          protectionType,
          protectionFee,
          paymentType,
          paymentMethod,
          contactInfo,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create purchase')
      }

      const { paymentId, clientSecret } = await response.json()

      if (paymentMethod === 'card') {
        await handleCardPayment(clientSecret, paymentId)
      } else {
        await handleAchPayment(clientSecret, paymentId)
      }
    } catch (error) {
      logError('Payment processing error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Payment failed'
      setPaymentError(errorMessage)
      onPaymentError(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        fontFamily: 'Sul Sans, Inter, system-ui, sans-serif',
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  }

  return (
    <form data-component-name="PaymentForm" onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Payment Summary</h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-blue-800">
              {paymentType === 'split' ? 'Initial Payment' : 'Subtotal'}:
            </span>
            <span className="text-blue-900">{formatAmount(baseAmount)}</span>
          </div>
          {protectionFee > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-blue-800">
                Care Protection ({protectionType === 'transfer' ? 'Transfer 3%' : 'Refund 6%'}):
              </span>
              <span className="text-blue-900">{formatAmount(protectionFee)}</span>
            </div>
          )}
          {surcharge > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-blue-800">Card Processing Fee (3%):</span>
              <span className="text-blue-900">{formatAmount(surcharge)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-1 border-t border-blue-200">
            <span className="text-blue-800 font-medium">Total:</span>
            <span className="font-bold text-blue-900">{formatAmount(totalAmount)}</span>
          </div>
        </div>
        {paymentType === 'split' && (
          <p className="text-xs text-blue-700 mt-2">
            You can complete the remaining payment later from your account.
          </p>
        )}
      </div>

      <div className="space-y-4">
        {paymentMethod === 'card' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Card Information</label>
            <div className="border border-gray-300 rounded-lg p-3 bg-white">
              <CardElement
                options={cardElementOptions}
                onChange={(event) => {
                  setCardError(event.error?.message || null)
                }}
              />
            </div>
            {cardError && <p className="text-red-600 text-sm mt-1">{cardError}</p>}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-primary mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <div>
                <h4 className="font-medium text-gray-900">Bank Account (ACH)</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Click the button below to securely connect your bank account. A secure window will
                  open where you can log in to your bank.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  <strong>Note:</strong> ACH payments take 2-5 business days to process.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500">
          Your payment information is secure and encrypted. We use Stripe to process payments
          safely.
        </div>
      </div>

      {paymentError && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{paymentError}</div>
      )}

      <Button
        type="submit"
        fullWidth
        disabled={!stripe || processing || verifyingPayment}
        className="relative"
      >
        {processing ? (
          <>
            <Spinner size="sm" color="text-white" className="mr-2" />
            {paymentMethod === 'ach' ? 'Connecting Bank...' : 'Processing Payment...'}
          </>
        ) : verifyingPayment ? (
          <>
            <Spinner size="sm" color="text-white" className="mr-2" />
            Confirming Payment...
          </>
        ) : paymentMethod === 'ach' ? (
          `Connect Bank & Pay ${formatAmount(totalAmount)}`
        ) : (
          `Pay ${formatAmount(totalAmount)}`
        )}
      </Button>
    </form>
  )
}
